import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { functionsService } from '@/services/functionsService';

export interface SectionChatContext {
  transcriptId?: string;
  companyId?: string;
  filingId?: string;
}

interface SectionChatProps {
  sectionContent: string;
  sectionTitle?: string;
  context?: SectionChatContext;
}

export const SectionChat: React.FC<SectionChatProps> = ({
  sectionContent,
  sectionTitle = 'this section',
  context: baseContext,
}) => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAsk = async () => {
    const q = question.trim();
    if (!q || !sectionContent?.trim()) return;
    setLoading(true);
    setError(null);
    setAnswer(null);
    try {
      const res = await functionsService.askCopilot(q, {
        ...baseContext,
        sectionContext: sectionContent,
      });
      setAnswer(res.answer ?? null);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as Error).message)
          : 'Something went wrong';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!sectionContent?.trim()) return null;

  return (
    <Card className="mt-4">
      <h4 className="sub-header text-sm text-gray-600 mb-2">Ask about {sectionTitle}</h4>
      <div className="flex gap-2 mb-2">
        <Input
          type="text"
          value={question}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuestion(e.target.value)}
          placeholder="e.g. What are the main risks?"
          className="flex-1"
          onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
        />
        <Button variant="primary" onClick={handleAsk} disabled={loading}>
          {loading ? '...' : 'Ask'}
        </Button>
      </div>
      {error && <p className="body-text text-sm text-red-600 mb-2">{error}</p>}
      {answer && (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="body-text text-sm text-gray-800 whitespace-pre-wrap">{answer}</p>
        </div>
      )}
    </Card>
  );
};
